import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import PatientSuggest from '../PatientSuggest';
import EmployeeSuggest from '../EmployeeSuggest';
import { BillingLineSelector } from '../shared/BillingLineSelector';
import { VERIFICATION_METHODS } from './constants';

/**
 * Modal for recording an EVV visit. Pure form — state lives in parent.
 * onChange(name, value), onSubmit(e), onClose().
 */
export const RecordVisitDialog = ({
  open,
  onClose,
  formData,
  employees,
  patients,
  onChange,
  onSubmit,
  saving,
}) => {
  const employeeDisplay = () => {
    if (!formData.employee_id) return '';
    const e = employees.find((x) => x.id === formData.employee_id);
    return e ? `${e.first_name} ${e.last_name}` : '';
  };
  const patientDisplay = () => {
    if (!formData.patient_id) return '';
    const p = patients.find((x) => x.id === formData.patient_id);
    return p ? `${p.first_name} ${p.last_name}` : '';
  };
  // Resolve the full patient (with billing_lines) so we can show the
  // patient's saved program/code combos in the BillingLineSelector.
  const selectedPatient =
    formData.patient_id && patients.find((x) => x.id === formData.patient_id);

  const handleSelectPatient = (p) => {
    onChange('patient_id', p.id);
    // Pre-select the patient's primary billing line.
    const full = patients.find((x) => x.id === p.id) || p;
    const primary = (Array.isArray(full?.billing_lines) && full.billing_lines[0]) || {
      payer_type: full?.payer_type,
      payer_program: full?.payer_program,
      procedure_code: full?.procedure_code,
    };
    if (primary?.payer_type) onChange('payer_type', primary.payer_type);
    if (primary?.payer_program) onChange('payer_program', primary.payer_program);
    if (primary?.procedure_code) onChange('service_code', primary.procedure_code);
  };

  // Memoize so BillingLineSelector doesn't see a fresh object on every
  // parent render unless one of the underlying fields actually changes.
  const billingLineValue = useMemo(() => ({
    payer_type: formData.payer_type,
    payer_program: formData.payer_program,
    service_code: formData.service_code,
  }), [formData.payer_type, formData.payer_program, formData.service_code]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Record EVV Visit</DialogTitle>
          <DialogDescription>
            Enter visit details for Electronic Visit Verification
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Employee *</Label>
            <EmployeeSuggest
              onSelect={(emp) => onChange('employee_id', emp.id)}
              placeholder="Type employee name..."
              value={employeeDisplay()}
            />
          </div>

          <div className="space-y-2">
            <Label>Patient *</Label>
            <PatientSuggest
              onSelect={handleSelectPatient}
              placeholder="Type patient name or Medicaid ID..."
              value={patientDisplay()}
            />
          </div>

          {/* Patient's saved billing lines — pre-selects the primary line
              so the EVV visit gets attributed to the right
              (payer_type, payer_program, code) combo, e.g. ODM / SP / G0156. */}
          <BillingLineSelector
            patient={selectedPatient || null}
            value={billingLineValue}
            onChange={({ payer_type, payer_program, service_code }) => {
              if (payer_type !== undefined) onChange('payer_type', payer_type || '');
              onChange('payer_program', payer_program || '');
              onChange('service_code', service_code || '');
            }}
            testIdPrefix="evv"
          />

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.service_date}
                onChange={(e) => onChange('service_date', e.target.value)}
                required
                data-testid="evv-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Time In *</Label>
              <Input
                type="time"
                value={formData.time_in}
                onChange={(e) => onChange('time_in', e.target.value)}
                required
                data-testid="evv-time-in"
              />
            </div>
            <div className="space-y-2">
              <Label>Time Out *</Label>
              <Input
                type="time"
                value={formData.time_out}
                onChange={(e) => onChange('time_out', e.target.value)}
                required
                data-testid="evv-time-out"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Verification Method *</Label>
            <Select
              value={formData.verification_method}
              onValueChange={(val) => onChange('verification_method', val)}
            >
              <SelectTrigger data-testid="evv-verification-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERIFICATION_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.verification_method === 'GPS' && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">GPS Coordinates</h4>
              <div className="grid grid-cols-2 gap-4">
                <GpsInput
                  label="Clock In Lat"
                  value={formData.clock_in_latitude}
                  testId="evv-clock-in-lat"
                  onChange={(v) => onChange('clock_in_latitude', v)}
                  placeholder="39.9612"
                />
                <GpsInput
                  label="Clock In Long"
                  value={formData.clock_in_longitude}
                  testId="evv-clock-in-long"
                  onChange={(v) => onChange('clock_in_longitude', v)}
                  placeholder="-82.9988"
                />
                <GpsInput
                  label="Clock Out Lat"
                  value={formData.clock_out_latitude}
                  testId="evv-clock-out-lat"
                  onChange={(v) => onChange('clock_out_latitude', v)}
                  placeholder="39.9612"
                />
                <GpsInput
                  label="Clock Out Long"
                  value={formData.clock_out_longitude}
                  testId="evv-clock-out-long"
                  onChange={(v) => onChange('clock_out_longitude', v)}
                  placeholder="-82.9988"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-indigo-900 hover:bg-indigo-800"
              disabled={saving}
              data-testid="save-evv-btn"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Record Visit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GpsInput = ({ label, value, testId, onChange, placeholder }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Input
      type="number"
      step="0.000001"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
    />
  </div>
);
